import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import classNames from "classnames";
import axios from "axios";
import { JurisdictionService, OrganizationOfficerConstraintsService } from "@services";
import utils from "@utils";
import { JURISDICTION_EXPIRES_OPTIONS } from "@constants/";
import AsyncReactSelect from "react-select/async";
import { Tooltip } from "../../../../../components";

const OfficerFulfillment = props => {
	const { form, updateForm, isNewJob } = props;
	const { lang } = global;
	const [useAgencyRestrictions, setUseAgencyRestrictions] = useState(false);
	const [agencyDefaults, setAgencyDefaults] = useState([]);
	const [AllJurisdictions, setAllJurisdictions] = useState([]);

	useEffect(() => {
		OrganizationOfficerConstraintsService.getAgencyRestrictions()
			.then(({ data }) => {
				let excludedAgencies = [];
				let requiredAgencies = [];
				if (data.ExcludedJurisdictions) {
					excludedAgencies = data.ExcludedJurisdictions.map(item => ({
						...item,
						ExpiresBefore: item.ExpiresBefore ?? 0,
						Visibility: "hide",
					}));
				}
				if (data.RequiredJurisdictions) {
					requiredAgencies = data.RequiredJurisdictions.map(item => ({
						...item,
						ExpiresBefore: item.ExpiresBefore ?? 0,
						Visibility: "show",
					}));
				}
				setAgencyDefaults([...requiredAgencies, ...excludedAgencies]);
				if (isNewJob)
					setUseAgencyRestrictions(
						data.ExcludedJurisdictions.length > 0 ||
							data.RequiredJurisdictions.length > 0,
					);
			})
			.catch(() => {
				utils.notify({
					type: "error",
					message: "Error retrieving jurisdiction restrictions",
					timeOut: 1500,
				});
			});
	}, []);

	useEffect(() => {
		if (form.ExcludedJurisdictions.length > 0 || form.RequiredJurisdictions.length > 0) {
			setAgencyDefaults([...form.RequiredJurisdictions, ...form.ExcludedJurisdictions]);
			// If we are editing a job/draft and jurisdictions are specified, change the radio button value to true
			if (!isNewJob) setUseAgencyRestrictions(form._usingRestrictions);
		}
	}, [form]);

	/** Validates all inputs in this section have been properly filled out and no required inputs have been missed */
	const validateForm = () => {
		const { validateSection } = props;
		let isValid = true;

		if (useAgencyRestrictions && AllJurisdictions.length === 0) {
			isValid = false;
		} else {
			AllJurisdictions.every(jur => {
				isValid =
					jur.ExpiresBefore !== "" &&
					jur.JurisdictionName !== "" &&
					jur.JurisdictionId !== "";
				return isValid;
			});
		}
		validateSection("officerFulfillment", isValid);
		return isValid;
	};

	// If we are editing a job/draft, we want all changes to be made instantly
	// For a brand new job, we want the user to go through the process section by section
	useEffect(() => {
		if (!isNewJob) {
			validateForm();
			const ExcludedJurisdictions = useAgencyRestrictions
				? AllJurisdictions.filter(item => item.Visibility === "hide")
				: [];
			const RequiredJurisdictions = useAgencyRestrictions
				? AllJurisdictions.filter(item => item.Visibility === "show")
				: [];
			updateForm("officerFulfillment", { ExcludedJurisdictions, RequiredJurisdictions });
		}
	}, [AllJurisdictions]);

	useEffect(() => {
		updateForm("officerFulfillment", { _usingRestrictions: useAgencyRestrictions });
		setAllJurisdictions(useAgencyRestrictions ? [...agencyDefaults] : []);
	}, [useAgencyRestrictions]);

	/**
	 * Loads a list of available jurisdictions for officer fulfillment.
	 * This method automatically filters out jurisdictons that have already been selected to eliminate the possibilty of contradicting rules for one jurisdiction.
	 * @param {String} inputValue The term used to search for jurisdictions
	 * @param {Function} callback A callback function to execute when results are ready to be handled
	 */
	const loadJurisdictions = (inputValue, callback) => {
		let cancelJurisdictions;
		if (inputValue.length < 3) {
			callback([]);
			return;
		}
		if (cancelJurisdictions) cancelJurisdictions();

		JurisdictionService.getAll({
			scope: "Name",
			search: inputValue,
			getCancelToken: c => {
				cancelJurisdictions = c;
			},
		})
			.then(response => {
				cancelJurisdictions = undefined;
				const selectedJurisdictionIds = [];
				AllJurisdictions.forEach(jurisdiction =>
					selectedJurisdictionIds.push(jurisdiction.JurisdictionId),
				);
				callback(
					response.data.map(option => ({
						value: option.Id,
						label: `${option.Name}, ${option.County} County ${option.StateName}`,
					})),
				);
			})
			.catch(error => {
				if (!axios.isCancel(error)) {
					utils.notify({
						type: "error",
						message: lang.t("Error loading jurisdictions"),
					});
					callback([]);
				}
			});
	};

	/** Adds an empty officer fulfillment rule to the current job */
	const handleAddRow = () => {
		const newAgency = {
			JurisdictionId: "",
			JurisdictionName: "",
			ExpiresBefore: "",
			Visibility: "show",
		};
		setAllJurisdictions(current => [...current, newAgency]);
	};

	/**
	 * Removes an officer fulfillment rule from the current job
	 * @param {Number} index The index of the rule to delete in the current array
	 */
	const handleRemoveRow = index => {
		setAllJurisdictions(AllJurisdictions.filter((item, idx) => idx !== index));
	};

	/**
	 * a
	 * @param {String} type a property to update for the chosen jurisdiction
	 * @param {Number} index The index in the array of jurisdictions to modify
	 * @returns {(event:Event) => void}
	 */
	const handleUpdateAgency = (type, index) => event => {
		let updates = {};
		switch (type) {
			case "selected": {
				const { value: JurisdictionId, label: JurisdictionName } = event;
				updates = { JurisdictionId, JurisdictionName };
				break;
			}
			case "visibility": {
				const {
					target: { value: Visibility },
				} = event;
				updates = { Visibility };
				break;
			}
			case "expiry": {
				const {
					target: { value: ExpiresBefore },
				} = event;
				updates = { ExpiresBefore: parseInt(ExpiresBefore, 10) };
				break;
			}
			default:
				break;
		}

		setAllJurisdictions(prev => {
			const newList = [...prev];
			newList[index] = { ...newList[index], ...updates };
			return newList;
		});
	};

	const handleContinue = () => {
		const { expandNextSection } = props;
		validateForm();

		const ExcludedJurisdictions = useAgencyRestrictions
			? AllJurisdictions.filter(item => item.Visibility === "hide")
			: [];
		const RequiredJurisdictions = useAgencyRestrictions
			? AllJurisdictions.filter(item => item.Visibility === "show")
			: [];
		updateForm("officerFulfillment", { ExcludedJurisdictions, RequiredJurisdictions });
		expandNextSection("officerFulfillment");
	};

	const agencyVisibility = [
		{ value: "show", label: lang.t("Show only to") },
		{ value: "hide", label: lang.t("Hide from") },
	];

	return (
		<div id="officer-fulfillment-container">
			<div className="row">
				<div className="col-xs-12">
					{lang.t("Is there a specific agency(ies) that you would like to use_")}
					<Tooltip
						content={lang.t(
							"If you want to pull from certain law enforcement agencies then list those agencies here and we_ll make sure only those officers can apply for the jobs_",
						)}
					>
						<i className="rkf rkf-question-solid job-form-tooltip tooltip-icon" />
					</Tooltip>
				</div>
			</div>
			<div className="row">
				<div className="col-xs-4 col-sm-2 radio">
					<label htmlFor="agencyRestrictions_no">
						<input
							type="radio"
							name="agencyRestrictions"
							id="agencyRestrictions_no"
							data-test-id="jobs__agency-restrictions-no"
							onChange={() => setUseAgencyRestrictions(false)}
							checked={useAgencyRestrictions === false}
						/>
						{lang.t("No")}
					</label>
				</div>
				<div className="col-xs-4 col-sm-2 radio">
					<label htmlFor="agencyRestrictions_yes">
						<input
							type="radio"
							name="agencyRestrictions"
							id="agencyRestrictions_yes"
							data-test-id="jobs__agency-restrictions-yes"
							onChange={() => setUseAgencyRestrictions(true)}
							checked={useAgencyRestrictions === true}
						/>
						{lang.t("Yes")}
					</label>
				</div>
			</div>

			<If condition={useAgencyRestrictions}>
				<If condition={AllJurisdictions.length > 0}>
					<div className="row hidden-xs restriction-headers">
						<div className="col-sm-3">{lang.t("Visibility")}</div>
						<div className="col-sm-4">{lang.t("Agency")}</div>
						<div className="col-sm-3 col-sm-offset-1">{lang.t("Time")}</div>
					</div>
				</If>
				<For
					each="agency"
					of={AllJurisdictions}
					index="agencyIndex"
				>
					<div
						key={`agencyJurisdiction-${agencyIndex}`}
						className="row agency-restriction"
					>
						<div className="col-sm-3">
							<select
								id={`JurisdictionVisibility-${agencyIndex}`}
								data-test-id="jobs__officer-fulfillment-agency-visibility"
								name={`JurisdictionVisibility-${agencyIndex}`}
								title="Visibility"
								className="form-control"
								value={agency.Visibility}
								onChange={handleUpdateAgency("visibility", agencyIndex)}
							>
								<For
									each="item"
									of={agencyVisibility}
								>
									<option
										key={`${item.label}-${item.value}-${agencyIndex}`}
										value={item.value}
									>
										{item.label}
									</option>
								</For>
							</select>
						</div>
						<div
							className={classNames("col-sm-4", {
								"invalid-field":
									agency.JurisdictionName === "" || agency.JurisdictionId === "",
							})}
						>
							<AsyncReactSelect
								key={`jurisdiction${agency.JurisdictionId}-${agencyIndex}`}
								id={`jurisdiction-${agencyIndex}`}
								classNamePrefix="react-select"
								title="Agency"
								placeholder={lang.t("Agency Name")}
								cacheOptions
								loadOptions={loadJurisdictions}
								defaultValue={
									agency.JurisdictionName !== ""
										? {
												value: agency.JurisdictionId,
												label: agency.JurisdictionName,
											}
										: ""
								}
								onChange={handleUpdateAgency("selected", agencyIndex)}
							/>
						</div>
						<div
							className={`col-xs-2 col-sm-1 text-center ${agency.ExpiresBefore === 0 ? "hidden-xs invisible" : ""}`}
						>
							<div className="until">{lang.t("Until")}</div>
						</div>
						<div
							className={classNames(
								"col-sm-3",
								agency.ExpiresBefore === 0 ? "col-xs-10" : "col-xs-8",
								{ "invalid-field": agency.ExpiresBefore === "" },
							)}
						>
							<select
								id={`all-jurisdiction-expiry-${agencyIndex}`}
								className="form-control"
								title="Time"
								data-test-id="jobs__officer-fulfillment-expiry"
								value={agency.ExpiresBefore}
								onChange={handleUpdateAgency("expiry", agencyIndex)}
							>
								<If condition={agency.ExpiresBefore === ""}>
									<option value="">Select a time</option>
								</If>
								<For
									each="option"
									of={JURISDICTION_EXPIRES_OPTIONS}
								>
									<option
										key={`${option.label}-${option.value}-${agencyIndex}`}
										value={option.value}
									>
										{option.label}
									</option>
								</For>
							</select>
						</div>
						<div className="col-xs-2 col-sm-1 text-center">
							<button
								type="button"
								className="btn btn-link"
								onClick={() => handleRemoveRow(agencyIndex)}
								data-test-id="jobs__officer-fulfillment-remove-agency"
							>
								<i className="rkf rkf-trash text-danger" />
							</button>
						</div>
					</div>
				</For>
				<div className="row">
					<button
						type="button"
						className="btn btn-link"
						title={lang.t("Add Agency")}
						onClick={() => handleAddRow()}
						data-test-id="jobs__officer-fulfillment-add-agency"
					>
						{`+ ${lang.t("Add Agency")}`}
					</button>
				</div>
			</If>
			<div className="text-center continue-button-container">
				<button
					type="button"
					id="officer-fulfillment-continue"
					onClick={() => handleContinue()}
					className="btn btn-primary btn-big"
					data-test-id="jobs__officer-fulfillment-continue"
				>
					{lang.t("Continue")}
				</button>
			</div>
		</div>
	);
};

OfficerFulfillment.propTypes = {
	form: PropTypes.object.isRequired,
	validateSection: PropTypes.func.isRequired,
	updateForm: PropTypes.func.isRequired,
	expandNextSection: PropTypes.func.isRequired,
	isNewJob: PropTypes.bool,
};

OfficerFulfillment.defaultProps = {
	isNewJob: false,
};

export default OfficerFulfillment;
